/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useHistory, useParams } from "react-router-dom";
import { Row } from "react-table";
import { Box, Message, Spacer } from "@nordcloud/gnui";
import { Table, FlexContainer, QueryPagination } from "~/components";
import { AssignmentRulesAdd } from "~/components/AssignmentRules/AssignmentRulesAdd";
import { useShowError } from "~/components/Error/useShowError";
import { LoaderWrap } from "~/components/LoaderWrap/LoaderWrap";
import { RouteParams } from "~/models";
import { ROUTES } from "~/routing";
import { useAxios, useQueryState } from "~/tools";
import { AssignmentRulesListColumns } from "~/views/customer/assignmentRules/AssignmentRulesList";

type AssignmentRule = {
  id: string;
  name: string;
};

function AssignmentRules() {
  const state = useParams<RouteParams>();
  const { customerId } = state;
  const [isLoading, setIsLoading] = React.useState(true);

  const [{ limit, page }] = useQueryState();
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const [assignmentRules, setAssignmentRules] = React.useState({
    list: [],
    totalCount: "",
  });

  const fetchAssignmentRules = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(
        `/customers/${customerId}/assignmentRules?limit=${limit}&page=${page}`
      );
      setAssignmentRules(() => ({
        list: data?.assignmentRules,
        totalCount: data?.totalCount,
      }));
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [Axios, customerId, limit, page, showError]);

  const columns = React.useMemo(
    () => AssignmentRulesListColumns({ customerId }),
    [customerId]
  );

  React.useEffect(() => {
    fetchAssignmentRules();
  }, [fetchAssignmentRules]);

  const handleRowKeyPress = (
    e: React.KeyboardEvent<HTMLTableRowElement>,
    selected: AssignmentRule
  ) => {
    if (e.key === "Enter") {
      redirectLink(selected.id);
    }
  };
  const onRedirectHandler = (row: AssignmentRule) => {
    redirectLink(row.id);
  };
  const history = useHistory();

  const redirectLink = (id: string) => {
    history.push(
      `${ROUTES.customers.index}/${customerId}/assignment-rules/${id}/`
    );
  };

  return (
    <Box>
      <FlexContainer justify="flex-end" css={{ margin: "0 1rem 0 auto" }}>
        <AssignmentRulesAdd />
      </FlexContainer>
      <Spacer height="1rem" />
      <Message
        css={{ marginBottom: "1rem" }}
        status="notification"
        image="info"
      >
        Assignment Rules are used to assign the same set of Services to multiple
        environments. Please keep in mind, that settings applied by Assignment
        Rule can be overwritten on Environment manually.
      </Message>

      <LoaderWrap loading={isLoading}>
        <>
          <Table
            getRowProps={(row: Row) => ({
              onClick: () => onRedirectHandler(row.original as AssignmentRule),
              onKeyPress: (e: React.KeyboardEvent<HTMLTableRowElement>) =>
                handleRowKeyPress(e, row.original as AssignmentRule),
              className: "interactive",
              tabIndex: 0,
              role: "button",
            })}
            columns={columns}
            data={assignmentRules.list || []}
            noDataText="No assignment rules."
          />
          {assignmentRules.list?.length > 0 && (
            <QueryPagination count={assignmentRules.list.length} />
          )}
        </>
      </LoaderWrap>
    </Box>
  );
}

export { AssignmentRules };
